import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, CalendarCheck } from "lucide-react";

const CALENDLY = "https://calendly.com/wordgumbolearningco";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADES = [
  "Pre-K",
  "Kindergarten",
  "1st Grade",
  "2nd Grade",
  "3rd Grade",
  "4th Grade",
  "5th Grade",
  "6th Grade or higher",
];

const SERVICES = [
  "Dyslexia Therapy Sessions",
  "Reading Tutoring Services",
  "Dyslexia Screener",
  "Full Dyslexia Core Assessment",
  "Not sure yet — please advise",
];

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k] || ""))
    .join("&");

export default function Contact() {
  const [form, setForm] = useState({
    parentName: "",
    email: "",
    phone: "",
    grade: "",
    service: "",
    message: "",
    "bot-field": "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form["bot-field"]) return; // honeypot
    setStatus("loading");
    setErrorMsg("");

    try {
      const host = typeof window !== "undefined" ? window.location.hostname : "";
      const isLocal = /^(localhost|127\.|0\.0\.0\.0)/.test(host);
      const isPreview = /emergentagent\.com$/i.test(host) || /\.preview\./i.test(host);
      const isStaticDeploy = !isLocal && !isPreview; // Netlify (or any other static host)

      if (isStaticDeploy) {
        // PRIMARY path on the deployed site: submit to Netlify Forms.
        // Netlify intercepts POSTs whose body contains form-name=<name of a detected form>.
        const resp = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: encode({ "form-name": "contact", ...form }),
        });
        if (!resp.ok) {
          throw new Error(`Form submission failed (${resp.status})`);
        }
      } else {
        // DEV / preview path: persist to backend (Mongo) so we can test locally.
        await axios.post(`${API}/contact`, {
          parentName: form.parentName,
          email: form.email,
          phone: form.phone,
          grade: form.grade,
          service: form.service,
          message: form.message,
        });
      }

      setStatus("success");
      setForm({ parentName: "", email: "", phone: "", grade: "", service: "", message: "", "bot-field": "" });
    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong. Please try again or email wordgumbolearningco@gmail.com.");
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <motion.aside
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-wg-purple/15">
            <span className="w-1.5 h-1.5 rounded-full bg-wg-green" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-wg-purple">Contact</span>
          </div>
          <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-wg-ink leading-tight">
            Let's chat about your child's <span className="text-wg-orange">reading journey.</span>
          </h2>
          <p className="mt-5 text-lg text-wg-ink2">
            Fill out the form and we'll be in touch — or skip ahead and pick a time that works for you.
          </p>

          <div className="mt-7 rounded-3xl bg-wg-cream2/70 border border-wg-purple/10 p-6">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-wg-purple text-white shrink-0"><MapPin className="w-5 h-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-wg-inkMuted font-semibold">Location</div>
                  <div className="text-wg-ink font-medium">Belle Chasse, Louisiana</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-wg-teal text-white shrink-0"><Mail className="w-5 h-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-wg-inkMuted font-semibold">Email</div>
                  <a href="mailto:wordgumbolearningco@gmail.com" className="text-wg-ink font-medium hover:text-wg-purple wg-link" data-testid="contact-email-link">
                    wordgumbolearningco@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-wg-orange text-white shrink-0"><Phone className="w-5 h-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-wg-inkMuted font-semibold">Phone</div>
                  <a href="tel:+15049494042" className="text-wg-ink font-medium hover:text-wg-purple wg-link" data-testid="contact-phone-link">
                    504-949-4042
                  </a>
                </div>
              </li>
            </ul>

            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-schedule-btn"
              className="mt-6 inline-flex items-center gap-2 bg-wg-purple text-white rounded-full px-6 py-3 font-semibold hover:bg-wg-purpleDeep hover:-translate-y-0.5 shadow-lg shadow-wg-purple/30 transition-all"
            >
              <CalendarCheck className="w-5 h-5" />
              Schedule Free Consultation
            </a>

            <p className="mt-4 text-sm text-wg-inkMuted">
              For the fastest response, please schedule a free consultation using the button above.
            </p>
          </div>
        </motion.aside>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            className="bg-white rounded-[2rem] border border-wg-purple/10 p-7 md:p-10 shadow-[0_18px_50px_-20px_rgba(74,29,82,0.15)]"
          >
            <input type="hidden" name="form-name" value="contact" />
            <p className="hidden">
              <label>Don't fill this out: <input name="bot-field" onChange={update("bot-field")} /></label>
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Parent/Guardian Name" required>
                <input
                  required
                  type="text"
                  name="parentName"
                  value={form.parentName}
                  onChange={update("parentName")}
                  data-testid="form-parent-name-input"
                  className="wg-input"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="form-email-input"
                  className="wg-input"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="form-phone-input"
                  className="wg-input"
                  placeholder="(504) 555-0123"
                />
              </Field>
              <Field label="Child's Grade">
                <select
                  name="grade"
                  value={form.grade}
                  onChange={update("grade")}
                  data-testid="grade-selector-dropdown"
                  className="wg-input bg-white"
                >
                  <option value="">Select grade…</option>
                  {GRADES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </Field>
              <Field label="Service Interested In" className="sm:col-span-2">
                <select
                  name="service"
                  value={form.service}
                  onChange={update("service")}
                  data-testid="form-service-select"
                  className="wg-input bg-white"
                >
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Message" className="sm:col-span-2">
                <textarea
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="form-message-textarea"
                  className="wg-input min-h-[140px]"
                  placeholder="Tell us a little about your child and what you're hoping for…"
                />
              </Field>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 bg-wg-purple text-white rounded-full px-7 py-4 font-semibold hover:bg-wg-purpleDeep hover:-translate-y-0.5 shadow-lg shadow-wg-purple/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {status === "loading" ? "Sending…" : "Send Message"}
              </button>

              {status === "success" && (
                <span className="inline-flex items-center gap-2 text-wg-green font-semibold" data-testid="contact-success-msg">
                  <CheckCircle2 className="w-5 h-5" />
                  Thank you! We'll be in touch soon.
                </span>
              )}
              {status === "error" && (
                <span className="text-red-600 font-medium" data-testid="contact-error-msg">{errorMsg}</span>
              )}
            </div>
          </form>

          <style>{`
            .wg-input {
              width: 100%;
              background: #FFFFFF;
              border: 2px solid #E9E3DC;
              border-radius: 1rem;
              padding: 0.85rem 1rem;
              font-family: Lexend, sans-serif;
              font-size: 1rem;
              color: #1F1221;
              transition: all 0.2s ease;
            }
            .wg-input:focus {
              outline: none;
              border-color: #0F766E;
              box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.15);
            }
          `}</style>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-semibold text-wg-ink mb-2">
        {label} {required && <span className="text-wg-orange">*</span>}
      </span>
      {children}
    </label>
  );
}
